import { get } from 'svelte/store';
import { ApiService } from '../api';
import { app } from '$lib/stores/app/store';
import { AOFL_PRODUCT_NAMES, PAGES } from '$lib/utils/constants';
import { goto } from '$app/navigation';
import { user } from '$lib/stores/user/store';

export class CrossAuthService {
	/**
	 * @description Redirects to the CTA page for the ABCmouse 2.0 app
	 * @param {string} gotoUrl - The URL to redirect to
	 * @returns {boolean} True if the redirect is to the CTA page for the ABCmouse 2.0 app, false otherwise
	 */
	static redirectToCTAPageApp2_0(gotoUrl) {
		return gotoUrl === 'LOCATION_2.0_DOWNLOAD';
	}

    /**
     * @description Validates if the error is related to the 2.0 account
     * @param {Error} error - The error object
     * @returns {boolean} True if the error is related to the 2.0 account, false otherwise
     */
    static isFirstTime2_0AccountLoginError(error) {
        return error?.errorCode === 992501 || error?.errorCode === 992004;
    }

    /**
     * @description Handles the first time 2.0 account login
     * @param {{ email: string; password: string; countryAbbrv: string; usernameType: string; }} credentialInfo - The credential information
     * @param {import('$lib/stores/user/store').UserStoreState} getUser - The user object
     * @param {Error} error - The error object
     * @returns {Promise<{ data: import('$lib/stores/user/store').UserStoreState; error?: object; playVoiceOver: boolean; }>} - The result of the login
     */
    static async firstTime2_0AccountLoginHandler(credentialInfo, getUser, error) {
        // This service is used to transfer the user from Abcmouse 2.0 to Abcmouse 1.0
        // Basically takes the user information from Abcmouse 2.0 and stores it to Abcmouse 1.0
        const legacyTransferResponse = await ApiService.legacyTransfer({
            username: credentialInfo.email,
            password: credentialInfo.password,
            country_abbrv: credentialInfo.countryAbbrv,
            username_type: credentialInfo.usernameType
        });

        // Get the short lived token for the legacy transfer user
        const shortLivedToken = await ApiService.getTransferUserShortLivedToken(legacyTransferResponse.token);

        // Set the app product to Abcmouse 2.0 state
        app.set({ ...get(app), global: { ...get(app).global, appProduct: AOFL_PRODUCT_NAMES.ABCMOUSE_2_0 } });

        // Update the user store with the legacy transfer token
        user.set({ ...getUser, shortLivedToken });

        await goto(PAGES.INTERNAL.DOWNLOAD_APP);
        
        return { data: getUser, error: error, playVoiceOver: true };
    }

    /**
     * @description Handles the 2.0 account login
     * @param {string} token - The token of the 2.0 account
     * @param {import('$lib/stores/user/store').UserStoreState} getUser - The user object
     * @returns {Promise<{ data: import('$lib/stores/user/store').UserStoreState; error?: object; playVoiceOver: boolean; }>} - The result of the login
     */
    static async handle2_0AccountLogin(token, getUser) {
        const shortLivedToken = await ApiService.getTransferUserShortLivedToken(token);

        // Update the user store with the legacy transfer token
        app.set({ ...get(app), global: { ...get(app).global, appProduct: AOFL_PRODUCT_NAMES.ABCMOUSE_2_0 } });
        user.set({ ...getUser, shortLivedToken });
        
        // Navigate to the download app page
        await goto(PAGES.INTERNAL.DOWNLOAD_APP);
        
        // The playVoiceOver is true because the user is being redirected to the download app page
        // Basically, we want to play the voice over when the user is being redirected to the download app page
        // This flag is handled here, because outside this function, the user interaction is required to play the voice over
        return { data: getUser, error: null, playVoiceOver: true };
    }
}